import { useEffect, useState } from "react";
import { getAllPersonas, actualizarFotoPerfil, getOnePersona, actualizarPersona } from "../../../services/persona.service";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import { getOneDepartamento } from "../../../services/departamento.service";
import { getOneProvincia } from "../../../services/provincia.service";
import { getOnePais } from "../../../services/pais.service";
import { useNavigate } from "react-router-dom";
import { showErrorAlert } from "../../../components/molecules/ShowAlert";
import { AdministradorNavbar } from "../../../components/common/navbar/AdministradorNavbar";
import { BASE_URL } from "../../../utils/configAxios";
import { getImagePerfil } from "../../../services/image.service";

const obtenerUsuarioLocal = () => {
    try {
        const user = JSON.parse(localStorage.getItem("userInfoES") || "{}");
        return user?.id ? user : null;
    } catch {
        return null;
    }
};

// Función para convertir archivo a base64
const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
        reader.readAsDataURL(file);
    });
};

const cacheKeys = (usuarioId, personaId) => [
    usuarioId ? `perfil_image_${usuarioId}` : null,
    personaId ? `perfil_image_persona_${personaId}` : null,
    personaId ? `perfil_image_${personaId}` : null // compatibilidad legacy
].filter(Boolean);

const readCachedImage = (usuarioId, personaId) => {
    try {
        for (const key of cacheKeys(usuarioId, personaId)) {
            const stored = localStorage.getItem(key);
            if (!stored) continue;

            const parsed = JSON.parse(stored);
            const image = typeof parsed === "string" ? parsed : parsed?.image;

            if (typeof image !== "string" || image.trim() === "") {
                localStorage.removeItem(key);
                continue;
            }

            if (image.startsWith("blob:")) {
                localStorage.removeItem(key);
                continue;
            }

            return {
                image,
                fileName: parsed?.fileName || null,
                rutaBD: parsed?.rutaBD || null,
            };
        }
    } catch (error) {
        console.error("Error al cargar imagen desde cache:", error);
    }
    return null;
};

const writeCachedImage = (usuarioId, personaId, data) => {
    const payload = JSON.stringify(data);
    cacheKeys(usuarioId, personaId).forEach((key) => localStorage.setItem(key, payload));
    window.dispatchEvent(new CustomEvent("imageUpdated"));
};

const buildAbsoluteUrl = (ruta) => {
    if (!ruta) return null;
    if (/^https?:\/\//i.test(ruta)) return ruta;
    return `${BASE_URL}/${ruta.replace(/^\/+/, "")}`;
};

export const PerfilAdmPage = () => {
    const [perfil, setPerfil] = useState(null);
    const navigate = useNavigate();
    
    // Reintenta traer imagen desde backend si inicialmente se usó localStorage
    const intentarRefrescarImagenDesdeBackend = async (idPersona) => {
        try {
            const personaActualizada = await getOnePersona(idPersona);
            if (personaActualizada?.imagen) {
                const partes = String(personaActualizada.imagen).split("/");
                const fileName = partes[partes.length - 1];
                try {
                    const url = await getImagePerfil(fileName);
                    if (url) {
                        setPerfil((prev) =>
                            prev
                                ? {
                                    ...prev,
                                    imagen: url,
                                    imagenRutaBD: personaActualizada?.imagen || prev.imagenRutaBD,
                                }
                                : prev
                        );
                        const usuario = obtenerUsuarioLocal();
                        if (usuario) {
                            writeCachedImage(usuario.id, idPersona, {
                                idUsuario: usuario.id,
                                idPersona,
                                image: url,
                                fileName,
                                rutaBD: personaActualizada?.imagen,
                                uploadDate: new Date().toISOString(),
                            });
                        }
                    }
                } catch (err) {
                    // Si es 404, no intentar más - el archivo no existe
                    if (err.isNotFound || err.status === 404) {
                        // Silenciar - ya sabemos que no existe
                        return;
                    }
                    // Para otros errores, lanzar para que se maneje arriba
                    throw err;
                }
            }
        } catch (err) {
            // Silenciar errores en segundo plano
        }
    };

    useEffect(() => {
        const fetchPerfil = async () => {
            const usuario = obtenerUsuarioLocal();
            if (!usuario) {
                return;
            }

            try {
                const personas = await getAllPersonas();
                const persona = personas.find((p) => p.usuarioid === usuario.id);

                if (!persona) {
                    return;
                }

                let departamento = null;
                let provincia = null;
                let pais = null;

                if (persona.idDepartamento) {
                    departamento = await getOneDepartamento(persona.idDepartamento);
                }
                if (persona.idProvincia) {
                    provincia = await getOneProvincia(persona.idProvincia);
                }
                if (persona.idPais) {
                    pais = await getOnePais(persona.idPais);
                }

                const cacheEntry = readCachedImage(usuario.id, persona.idPersona);
                let imagenUrl = cacheEntry?.image || null;
                let imagenRutaBD = persona.imagen || cacheEntry?.rutaBD || null;
                let debeRefrescar = false;

                if (persona.imagen && persona.imagen.trim() !== "" && persona.imagen !== "null") {
                    const partes = persona.imagen.split("/");
                    const fileName = partes[partes.length - 1];
                    try {
                        imagenUrl = await getImagePerfil(fileName);
                        imagenRutaBD = persona.imagen;
                        writeCachedImage(usuario.id, persona.idPersona, {
                            idUsuario: usuario.id,
                            idPersona: persona.idPersona,
                            image: imagenUrl,
                            fileName,
                            rutaBD: persona.imagen,
                            uploadDate: new Date().toISOString(),
                        });
                    } catch (e) {
                        if (e.isNotFound || e.status === 404) {
                            imagenUrl = cacheEntry?.image || null;
                        } else {
                            imagenUrl =
                                cacheEntry?.image ||
                                (e.isCorsError ? null : buildAbsoluteUrl(persona.imagen));
                            debeRefrescar = !e.isCorsError && Boolean(cacheEntry?.image);
                        }
                    }
                } else {
                    imagenUrl = cacheEntry?.image || null;
                    debeRefrescar = Boolean(imagenUrl);
                }

                const perfilFormateado = {
                    idPersona: persona.idPersona,
                    nombre: persona.nombre,
                    apellido: persona.apellido,
                    username: usuario.username,
                    telefono: persona.celular,
                    ci: persona.ci || "No registrado",
                    genero: persona.sexo || "No registrado",
                    correo: persona.correo || "No registrado",
                    fechaNacimiento: persona.fechaNacimiento?.split("T")[0] || "No registrado",
                    pais: pais?.nombre || "Bolivia",
                    departamento: departamento?.nombre || "No registrado",
                    provincia: provincia?.nombre || "No registrado",
                    imagen: imagenUrl,
                    imagenRutaBD: imagenRutaBD,
                };

                setPerfil(perfilFormateado);

                if (debeRefrescar) {
                    setTimeout(() => intentarRefrescarImagenDesdeBackend(persona.idPersona), 2000);
                }
            } catch (error) {
                console.error("Error al obtener el perfil del administrador:", error);
            }
        };

        fetchPerfil();
    }, []);

    const rolUsuario = JSON.parse(localStorage.getItem("userInfoES") || "{}")?.rol;
    const cargo = rolUsuario === "SADM" ? "Super Administrador" : "Administrador";
    

    if (!perfil) {
        return (
            <ContenedorPageTemplate titulo="Mi Perfil">
                <p className="text-center text-gray-600">Cargando datos personales...</p>
            </ContenedorPageTemplate>
        );
    }

    // Iconos para cada tipo de dato
    const getIcon = (label) => {
        const iconMap = {
            "Cédula de Identidad": "🆔",
            "Fecha de Nacimiento": "📅",
            "Género": "👤",
            "Correo electrónico": "✉️",
            "Teléfono": "📱",
            "País": "🌎",
            "Departamento": "📍",
            "Provincia": "🗺️",
        };
        return iconMap[label] || "📋";
    };

    const PerfilDato = ({ label, valor }) => {
        return (
            <div className="group relative p-5 rounded-xl shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1 bg-white border-2 border-gray-100 hover:border-theme-blue/30">
                <div className="flex items-start gap-3">
                    <div className="text-2xl flex-shrink-0 bg-theme-blue/10 p-2 rounded-lg">
                        {getIcon(label)}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wide mb-2 text-gray-500">
                            {label}
                        </p>
                        <p className="text-base font-bold break-words text-gray-800">
                            {valor}
                        </p>
                    </div>
                </div>
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
            </div>
        );
    };

    return (
        <div className="bg-gradient-to-br from-blue-50 via-white to-yellow-50 min-h-screen">
            <AdministradorNavbar />
            <ContenedorPageTemplate fondo={false}>
                {/* Header con gradiente */}
                <div className="max-w-6xl mx-auto mt-8 mb-8">
                    <div className="bg-gradient-to-r from-theme-blue via-blue-600 to-theme-yellow rounded-2xl shadow-2xl overflow-hidden">
                        <div className="bg-white/95 backdrop-blur-sm p-8 md:p-12">
                            <div className="flex flex-col md:flex-row items-center gap-8">
                                {/* Avatar grande */}
                                <div className="relative w-32 h-32 md:w-40 md:h-40 group flex-shrink-0">
                                    <div className="absolute inset-0 bg-gradient-to-br from-theme-blue to-theme-yellow rounded-full p-1">
                                        <div className="w-full h-full bg-white rounded-full p-1">
                                            <img
                                                src={
                                                    perfil.imagen || "https://cdn-icons-png.flaticon.com/512/847/847969.png"
                                                }
                                                alt="Foto de perfil"
                                                className="w-full h-full rounded-full object-cover"
                                                onError={(e) => {
                                                    console.error("Error al cargar imagen:", e.target.src);
                                                    e.target.src = "https://cdn-icons-png.flaticon.com/512/847/847969.png";
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Hover + ícono */}
                                    <label
                                        htmlFor="uploadFotoAdm"
                                        className="absolute inset-0 bg-gradient-to-br from-theme-blue/80 to-theme-yellow/80 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center cursor-pointer backdrop-blur-sm"
                                        style={{
                                            zIndex: 2
                                        }}
                                        title="Cambiar foto"
                                    >
                                        <div className="bg-white rounded-full p-3 shadow-lg">
                                            <svg className="w-6 h-6 text-theme-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                        </div>
                                    </label>

                        <input
                            type="file"
                            id="uploadFotoAdm"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                                const file = e.target.files[0];
                                if (!file) return;

                                // Obtener usuario actual
                                const usuarioActual = obtenerUsuarioLocal();
                                if (!usuarioActual) {
                                    showErrorAlert({
                                        title: "Error de sesión",
                                        text: "No se encontró la información del usuario. Recarga la página e intenta nuevamente.",
                                    });
                                    return;
                                }

                                // Validaciones más estrictas
                                const validExtensions = ["image/jpeg", "image/png", "image/jpg"];
                                const validFileExtensions = [".jpg", ".jpeg", ".png"];
                                const maxSize = 5 * 1024 * 1024; // 5MB

                                // Validar tipo MIME
                                if (!validExtensions.includes(file.type)) {
                                    showErrorAlert({
                                        title: "Formato inválido",
                                        text: "Solo se permiten imágenes JPG, JPEG o PNG.",
                                    });
                                    e.target.value = ""; // Limpiar input
                                    return;
                                }

                                // Validar extensión del archivo
                                const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
                                if (!validFileExtensions.includes(fileExtension)) {
                                    showErrorAlert({
                                        title: "Extensión inválida",
                                        text: "El archivo debe tener extensión .jpg, .jpeg o .png",
                                    });
                                    e.target.value = "";
                                    return;
                                }

                                // Validar tamaño
                                if (file.size > maxSize) {
                                    showErrorAlert({
                                        title: "Tamaño excedido",
                                        text: "La imagen no debe superar los 5MB.",
                                    });
                                    e.target.value = "";
                                    return;
                                }

                                // Validar que el archivo no esté vacío
                                if (file.size === 0) {
                                    showErrorAlert({
                                        title: "Archivo vacío",
                                        text: "El archivo seleccionado está vacío.",
                                    });
                                    e.target.value = "";
                                    return;
                                }

                                // Validar que tengamos el ID de persona
                                if (!perfil?.idPersona) {
                                    showErrorAlert({
                                        title: "Error de datos",
                                        text: "No se encontró el ID de la persona. Recarga la página e intenta nuevamente.",
                                    });
                                    return;
                                }

                                // Declarar loadingAlert fuera del try-catch
                                let loadingAlert = null;

                                try {
                                    // Mostrar loading
                                    loadingAlert = showErrorAlert({
                                        title: "Procesando imagen...",
                                        text: "Por favor espera mientras se procesa la imagen.",
                                        showCancelButton: false,
                                        showConfirmButton: false,
                                        allowOutsideClick: false,
                                        didOpen: () => {
                                            // No cerrar automáticamente
                                        }
                                    });

                                    // Subir al backend
                                    const formData = new FormData();
                                    // Usar mayúsculas para coincidir con el backend (IdPersona, Foto)
                                    formData.append("IdPersona", perfil.idPersona);
                                    formData.append("Foto", file);
                                    
                                    // Debug: verificar FormData antes de enviar
                                    console.log("📤 FormData preparado:");
                                    console.log("  - IdPersona:", perfil.idPersona);
                                    console.log("  - Foto:", file.name, `(${file.size} bytes, ${file.type})`);
                                    for (let [key, value] of formData.entries()) {
                                        if (value instanceof File) {
                                            console.log(`  - ${key}:`, value.name, `(${value.size} bytes)`);
                                        } else {
                                            console.log(`  - ${key}:`, value);
                                        }
                                    }

                                    let nuevaImagenUrl = null;
                                    let persistidoEnBackend = false;
                                    let personaActualizada = null; // Declarar fuera del try para que esté disponible después
                                    const rutaImagenAnterior = perfil?.imagenRutaBD || null;

                                    try {
                                        // Subir imagen al backend
                                        console.log("🌐 Intentando subir imagen al backend...");
                                        await actualizarFotoPerfil(formData);
                                        console.log("✅ Respuesta del backend recibida (HTTP 200)");

                                        // Hacer múltiples intentos de verificación con esperas progresivas
                                        let intentos = 0;
                                        const maxIntentos = 5;
                                        
                                        while (intentos < maxIntentos && (
                                            !personaActualizada?.imagen ||
                                            personaActualizada.imagen === "null" ||
                                            (typeof personaActualizada?.imagen === 'string' && personaActualizada.imagen.trim() === "") ||
                                            (rutaImagenAnterior && personaActualizada.imagen === rutaImagenAnterior)
                                        )) {
                                            intentos++;
                                            const espera = intentos * 500; // 500ms, 1000ms, 1500ms, 2000ms, 2500ms
                                            console.log(`⏳ Esperando ${espera}ms antes del intento ${intentos}/${maxIntentos} de verificación...`);
                                            await new Promise(resolve => setTimeout(resolve, espera));
                                            
                                            console.log(`🔍 Intentando ${intentos}/${maxIntentos}: Reconsultando persona para verificar imagen guardada...`);
                                            personaActualizada = await getOnePersona(perfil.idPersona);
                                            console.log(`📊 Resultado intento ${intentos}:`, {
                                                tieneImagen: !!personaActualizada?.imagen,
                                                imagen: personaActualizada?.imagen,
                                                esNull: personaActualizada?.imagen === "null",
                                                esVacio: typeof personaActualizada?.imagen === 'string' ? personaActualizada.imagen.trim() === "" : false
                                            });
                                        }

                                        if (personaActualizada?.imagen && personaActualizada.imagen !== "null" && (typeof personaActualizada.imagen === 'string' && personaActualizada.imagen.trim() !== "")) {
                                            const partes = String(personaActualizada.imagen).split("/");
                                            const fileNamePersistido = partes[partes.length - 1];
                                            console.log("📝 FileName extraído de BD:", fileNamePersistido);
                                            
                                            try {
                                                console.log("🌐 Cargando imagen desde servidor con getImagePerfil...");
                                                
                                                nuevaImagenUrl = await getImagePerfil(fileNamePersistido);
                                                persistidoEnBackend = true;
                                                console.log("✅✅✅ ÉXITO TOTAL: Imagen cargada desde backend:", nuevaImagenUrl?.substring(0, 50) + "...");
                                            } catch (errGet) {
                                                // Si es 404, el archivo no se encuentra (problema serio después de subir)
                                                if (errGet.isNotFound || errGet.status === 404) {
                                                    console.error("🚫 ERROR 404: La imagen se subió pero no se encuentra en el servidor");
                                                    console.error("💡 Posibles causas:");
                                                    console.error("   1. El archivo no se guardó correctamente en el servidor");
                                                    console.error("   2. El routing del backend no está configurado correctamente");
                                                    console.error("   3. Directory.GetCurrentDirectory() no apunta al directorio correcto");
                                                    
                                                    // Usar preview local como fallback
                                                    nuevaImagenUrl = await convertFileToBase64(file);
                                                    persistidoEnBackend = false;
                                                } else if (errGet.response?.status === 401) {
                                                    console.error("🔐 ERROR DE AUTENTICACIÓN: El token no está siendo aceptado");
                                                    // Usar preview local como fallback
                                                    nuevaImagenUrl = await convertFileToBase64(file);
                                                    persistidoEnBackend = false;
                                                } else {
                                                    // Otro tipo de error (red, etc.)
                                                    console.error("❌ Error al cargar imagen desde endpoint API:", errGet.message);
                                                    
                                                    // Fallback: intentar URL absoluta directa
                                                    try {
                                                        const urlAbsoluta = buildAbsoluteUrl(`images/perfil/${fileNamePersistido}`);
                                                        const response = await fetch(urlAbsoluta);
                                                        if (response.ok) {
                                                            nuevaImagenUrl = urlAbsoluta;
                                                            persistidoEnBackend = true;
                                                            console.log("✅ Imagen cargada desde URL absoluta directa");
                                                        } else {
                                                            throw new Error(`HTTP ${response.status}`);
                                                        }
                                                    } catch (errFetch) {
                                                        console.warn("⚠️ URL absoluta también falló, usando preview local");
                                                        // Último fallback: preview local
                                                        nuevaImagenUrl = await convertFileToBase64(file);
                                                        persistidoEnBackend = false;
                                                    }
                                                }
                                            }
                                        } else {
                                            console.error("❌❌❌ PROBLEMA: Backend NO actualizó persona.imagen en la base de datos después de", maxIntentos, "intentos");
                                            console.error("Estado final de persona:", personaActualizada);
                                            // Lanzar error para que se muestre al usuario
                                            throw new Error(`El servidor recibió la imagen pero NO la guardó en la base de datos. Campo 'imagen' sigue siendo: ${personaActualizada?.imagen || 'undefined'}. Verifica los logs del backend.`);
                                        }
                                    } catch (errSubida) {
                                        console.error("❌ Error al subir imagen al backend:", errSubida);
                                        
                                        // Mostrar error específico al usuario
                                        if (loadingAlert) loadingAlert.close();
                                        
                                        showErrorAlert({
                                            title: "Error al subir imagen",
                                            text: errSubida.message || "No se pudo guardar la imagen en el servidor. Verifica los permisos del servidor o intenta más tarde.",
                                        });
                                        
                                        // Limpiar input
                                        e.target.value = "";
                                        return; // Salir sin guardar en localStorage
                                    }

                                    // Si llegamos aquí y no hay URL, usar preview local (no debería pasar)
                                    if (!nuevaImagenUrl) {
                                        console.warn("No se obtuvo ninguna URL de imagen, usando preview local");
                                        nuevaImagenUrl = await convertFileToBase64(file);
                                    }

                                    // Obtener el fileName correcto de la BD si está disponible
                                    let fileNameCorrecto = file.name;
                                    if (personaActualizada?.imagen) {
                                        const partes = String(personaActualizada.imagen).split("/");
                                        fileNameCorrecto = partes[partes.length - 1];
                                    }
                                    
                                    // Limpiar localStorage de imágenes antiguas antes de guardar la nueva
                                    localStorage.removeItem(`perfil_image_${usuarioActual.id}`);
                                    localStorage.removeItem(`perfil_image_persona_${perfil.idPersona}`);
                                    
                                    // Guardar en localStorage solo como caché (si persistió en backend, preferir URL del servidor)
                                    const imageData = {
                                        idUsuario: usuarioActual.id,
                                        idPersona: perfil.idPersona,
                                        image: nuevaImagenUrl,
                                        fileName: fileNameCorrecto, // Usar el fileName de la BD, no el original
                                        rutaBD: personaActualizada?.imagen || fileNameCorrecto,
                                        uploadDate: new Date().toISOString(),
                                        persistidoEnBackend: persistidoEnBackend
                                    };
                                    
                                    // Guardar en localStorage
                                    localStorage.setItem(`perfil_image_${usuarioActual.id}`, JSON.stringify(imageData));
                                    localStorage.setItem(`perfil_image_persona_${perfil.idPersona}`, JSON.stringify(imageData));
                                    
                                    // Cerrar loading
                                    if (loadingAlert) loadingAlert.close();

                                    console.log("Imagen procesada:", { 
                                        persistidoEnBackend, 
                                        nuevaImagenUrl: nuevaImagenUrl?.substring(0, 50) + "...",
                                        fileNameCorrecto 
                                    });

                                    // Actualizar imagen en el estado con la URL final
                                    setPerfil((prev) => ({ ...prev, imagen: nuevaImagenUrl, imagenRutaBD: personaActualizada?.imagen || prev?.imagenRutaBD }));

                                    // Disparar evento para actualizar navbar
                                    window.dispatchEvent(new CustomEvent('imageUpdated'));

                                    // Mostrar éxito con mensaje según persistencia
                                    showErrorAlert({
                                        title: persistidoEnBackend ? "¡Éxito!" : "⚠️ Imagen guardada temporalmente",
                                        text: persistidoEnBackend 
                                            ? "La imagen se ha actualizado correctamente en el servidor." 
                                            : "La imagen se muestra localmente, pero no se pudo guardar en el servidor. Verifica los permisos.",
                                        icon: persistidoEnBackend ? "success" : "warning"
                                    });

                                } catch (error) {
                                    console.error("Error al procesar la imagen:", error);

                                    // Cerrar loading si existe
                                    if (loadingAlert) loadingAlert.close();

                                    showErrorAlert({
                                        title: "Error al procesar imagen",
                                        text: "No se pudo procesar la imagen. Intenta con otro archivo.",
                                    });

                                    // Limpiar input en caso de error
                                    e.target.value = "";
                                }
                            }}
                        />
                                </div>

                                {/* Nombre y correo */}
                                <div className="flex-1 text-center md:text-left">
                                    <h1 className="text-3xl md:text-4xl font-black text-theme-blue mb-2">
                                        {perfil.nombre} {perfil.apellido}
                                    </h1>
                                    <div className="inline-flex items-center gap-2 bg-theme-yellow/20 px-4 py-2 rounded-full mb-4">
                                        <span className="text-theme-blue font-bold">{cargo}</span>
                                        <span className="text-theme-blue/70">•</span>
                                        <span className="text-theme-blue font-semibold">{perfil.username}</span>
                                    </div>
                                    <button
                                        onClick={() => navigate("/perfilAdm/editarPerfilAdm")}
                                        className="bg-gradient-to-r from-theme-blue to-blue-600 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-xl shadow-lg font-semibold transition-all duration-300 hover:shadow-xl hover:scale-105 flex items-center gap-2 mx-auto md:mx-0"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        Actualizar perfil
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contenedor de datos */}
                <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-8">
                    {/* Título de sección */}
                    <div className="mb-6">
                        <h2 className="text-2xl font-bold text-theme-blue mb-2 flex items-center gap-2">
                            <span className="bg-gradient-to-r from-theme-blue to-theme-yellow w-1 h-8 rounded-full"></span>
                            Información Personal
                        </h2>
                        <p className="text-gray-600 text-sm">Gestiona y actualiza tu información personal</p>
                    </div>

                    {/* Datos en grid estilo tarjeta */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                        <PerfilDato label="Cédula de Identidad" valor={perfil.ci} />
                        <PerfilDato label="Fecha de Nacimiento" valor={perfil.fechaNacimiento} />
                        <PerfilDato label="Género" valor={perfil.genero} />
                        <PerfilDato label="Correo electrónico" valor={perfil.correo} />
                        <PerfilDato label="Teléfono" valor={perfil.telefono} />
                    </div>

                    {/* Sección de ubicación */}
                    <div className="mt-8 pt-8 border-t-2 border-gray-100">
                        <h2 className="text-2xl font-bold text-theme-blue mb-6 flex items-center gap-2">
                            <span className="bg-gradient-to-r from-theme-blue to-theme-yellow w-1 h-8 rounded-full"></span>
                            Ubicación
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
                            <PerfilDato label="País" valor={perfil.pais} />
                            <PerfilDato label="Departamento" valor={perfil.departamento} />
                            <PerfilDato label="Provincia" valor={perfil.provincia} />
                        </div>
                    </div>
                </div>
            </ContenedorPageTemplate>
        </div>
    );
}